import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { RichEditor } from "@/components/RichEditor.tsx"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field.tsx"
import { Input } from "@/components/ui/input.tsx"
import { useMutation, useQuery } from "@tanstack/react-query"
import { listsApi } from "@/api/lists.api.ts"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxValue,
  ComboboxItem,
} from "@/components/ui/combobox.tsx"
import { Button } from "@/components/ui/button.tsx"
import { FileUpload } from "@/components/ui/file-upload.tsx"
import { pitchesApi } from "@/api/pitches.api.ts"
import { countryCodeToFlag, uniqueById } from "@/lib/utils.ts"
import { useTranslation } from "react-i18next"
import { PitchGuidelines } from "@/components/PitchGuidelines.tsx"

export const Route = createFileRoute("/_protected/pitch/create")({
  ssr: false,
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { callId }: { callId: string } = useSearch({
    from: "/_protected/pitch/create",
  })
  const [pitchTitle, setPitchTitle] = useState<string>("")
  const [pitchContent, setPitchContent] = useState<string>("")
  const [languagesValue, setLanguagesValue] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [formatsValue, setFormatsValue] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [countriesValue, setCountriesValue] = useState<
    Array<{ id: number; name: string; flag: string }>
  >([])
  const [themesValue, setThemesValue] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [attachments, setAttachments] = useState<Array<any>>([])
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "processing" | "error" | "done"
  >("idle")

  const [isSaving, setIsSaving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formats = useQuery({
    queryKey: ["getFormats"],
    queryFn: () => listsApi.getFormats(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
  const themes = useQuery({
    queryKey: ["getThemes"],
    queryFn: () => listsApi.getThemes(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
  const countries = useQuery({
    queryKey: ["getCountries"],
    queryFn: () => listsApi.getCountries(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
  const languages = useQuery({
    queryKey: ["getLanguages"],
    queryFn: () => listsApi.getLanguages(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const createPitch = useMutation({
    mutationFn: pitchesApi.create,
  })

  const savePitch = useMutation({
    mutationFn: pitchesApi.save,
  })

  const uploadAttachment = useMutation({
    mutationFn: pitchesApi.uploadAttachment,
  })

  const onChangeAttachments = (files) => {
    setAttachments(files)
  }

  const onChangeLanguages = (langs) => {
    setLanguagesValue(uniqueById(langs))
  }
  const onChangeThemes = (topics) => {
    setThemesValue(uniqueById(topics))
  }
  const onChangeFormats = (types) => {
    setFormatsValue(uniqueById(types))
  }
  const onChangeLocation = (locs) => {
    setCountriesValue(uniqueById(locs))
  }

  useEffect(() => {
    setTitle(t("create.pageTitle"))
  }, [setTitle])

  const getFormData = (attachments?: Array<any>) => ({
    title: pitchTitle,
    descriptionHtml: pitchContent,
    themeIds: themesValue.map((item) => item.id),
    formatIds: formatsValue.map((item) => item.id),
    languageIds: languagesValue.map((item) => item.id),
    locationCountryIds: countriesValue.map((item) => item.id),
    attachments: attachments || [],
    callId: callId || null,
  })

  const ValidateFormData = () => {
    return (
      pitchTitle !== "" &&
      pitchContent !== "" &&
      themesValue.length > 0 &&
      formatsValue.length > 0 &&
      languagesValue.length > 0 &&
      countriesValue.length > 0
    )
  }

  const onClickSave = async () => {
    setIsSaving(true)
    if (attachments.length > 0) {
      setUploadState("uploading")
      const uploadedAttachments: Array<any> = []

      for (const file of attachments) {
        const formData = new FormData()
        formData.append("file", file)

        await uploadAttachment.mutateAsync(formData, {
          onSuccess: (data) => {
            if (data) {
              uploadedAttachments.push({
                url: data?.result?.url,
                attachmentType: data?.result?.attachmentType,
                fileName: data?.result?.fileName,
                fileSizeBytes: data?.result?.fileSizeBytes,
              })
            }
          },
        })
      }
      setUploadState("done")
      const saveData = getFormData(uploadedAttachments)
      savePitch.mutate(saveData as any, {
        onSuccess: async (data: any) => {
          await navigate({ to: `/pitch/${data?.result.publicId}` })
        },
      })
    } else {
      const saveData = getFormData()
      savePitch.mutate(saveData as any, {
        onSuccess: async (data) => {
          await navigate({ to: `/pitch/${data.result.publicId}` })
        },
      })
    }
  }

  const onClickSubmit = async () => {
    setIsSubmitting(true)
    if (attachments.length > 0) {
      setUploadState("uploading")
      const uploadedAttachments: Array<any> = []

      for (const file of attachments) {
        const formData = new FormData()
        formData.append("file", file)
        await uploadAttachment.mutateAsync(formData, {
          onSuccess: (data) => {
            if (data) {
              uploadedAttachments.push({
                url: data?.result?.url,
                attachmentType: data?.result?.attachmentType,
                fileName: data?.result?.fileName,
                fileSizeBytes: data?.result?.fileSizeBytes,
              })
            }
          },
        })
      }
      setUploadState("done")
      const saveData = getFormData(uploadedAttachments)
      createPitch.mutate(saveData as any, {
        onSuccess: async (data: any) => {
          await navigate({ to: `/pitch/${data?.result.publicId}` })
        },
      })
    } else {
      const saveData = getFormData()
      createPitch.mutate(saveData as any, {
        onSuccess: async (data: any) => {
          await navigate({ to: `/pitch/${data?.result.publicId}` })
        },
      })
    }
  }

  return (
    <div className="w-full">
      <PitchGuidelines />
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="title">{t("create.title")}</FieldLabel>
          <Input
            id="title"
            type="text"
            placeholder={t("create.titlePlaceholder")}
            required
            className="h-10"
            onChange={(e) => setPitchTitle(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="content">{t("create.content")}</FieldLabel>
          <RichEditor onChange={setPitchContent} />
        </Field>
        <Field>
          <FieldLabel htmlFor="attachments">
            {t("create.attachments")}
          </FieldLabel>
          <FileUpload
            value={attachments}
            onChange={onChangeAttachments}
            accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            multiple
            maxFiles={10}
            state={uploadState}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="languages">{t("create.lang")}</FieldLabel>
          <Combobox
            items={languages?.data?.result?.items || []}
            multiple
            value={languagesValue}
            onValueChange={onChangeLanguages}
            id="languages"
          >
            <ComboboxChips>
              <ComboboxValue>
                {languagesValue?.map((item) => (
                  <ComboboxChip key={item.name}>{item.name}</ComboboxChip>
                ))}
              </ComboboxValue>
              <ComboboxChipsInput
                placeholder={`${t("create.select")}${t("create.lang")}`}
              />
            </ComboboxChips>
            <ComboboxContent>
              <ComboboxEmpty></ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem
                    key={item.name}
                    value={{ id: item.id, name: item.name }}
                  >
                    {item.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
        <Field>
          <FieldLabel htmlFor="formats">{t("create.format")}</FieldLabel>
          <Combobox
            items={formats?.data?.result?.items || []}
            multiple
            value={formatsValue}
            onValueChange={onChangeFormats}
            id="formats"
          >
            <ComboboxChips>
              <ComboboxValue>
                {formatsValue?.map((item) => (
                  <ComboboxChip key={item.name}>{item.name}</ComboboxChip>
                ))}
              </ComboboxValue>
              <ComboboxChipsInput
                placeholder={`${t("create.select")}${t("create.format")}`}
              />
            </ComboboxChips>
            <ComboboxContent>
              <ComboboxEmpty></ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem
                    key={item.name}
                    value={{ id: item.id, name: item.name }}
                  >
                    {item.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
        <Field>
          <FieldLabel htmlFor="location">{t("create.loc")}</FieldLabel>
          <Combobox
            items={
              countries?.data?.result?.items.map((item) => ({
                ...item,
                flag: countryCodeToFlag(item.code),
              })) || []
            }
            multiple
            value={countriesValue}
            onValueChange={onChangeLocation}
            id="location"
          >
            <ComboboxChips>
              <ComboboxValue>
                {countriesValue?.map((item) => (
                  <ComboboxChip key={item.name}>
                    {item.flag} {item.name}
                  </ComboboxChip>
                ))}
              </ComboboxValue>
              <ComboboxChipsInput
                placeholder={`${t("create.select")}${t("create.loc")}`}
              />
            </ComboboxChips>
            <ComboboxContent>
              <ComboboxEmpty>{t("create.noItem")}</ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem
                    key={item.name}
                    value={{ id: item.id, name: item.name, flag: item.flag }}
                  >
                    {item.flag} {item.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
        <Field>
          <FieldLabel htmlFor="themes">{t("create.themes")}</FieldLabel>
          <Combobox
            items={themes?.data?.result?.items || []}
            multiple
            value={themesValue}
            onValueChange={onChangeThemes}
            id="themes"
          >
            <ComboboxChips>
              <ComboboxValue>
                {themesValue?.map((item) => (
                  <ComboboxChip key={item.name}>{item.name}</ComboboxChip>
                ))}
              </ComboboxValue>
              <ComboboxChipsInput
                placeholder={`${t("create.select")}${t("create.themes")}`}
              />
            </ComboboxChips>
            <ComboboxContent>
              <ComboboxEmpty>{t("create.noItem")}</ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem
                    key={item.name}
                    value={{ id: item.id, name: item.name }}
                  >
                    {item.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>
      </FieldGroup>
      <div className="mt-4 mb-8 flex justify-end gap-2">
        <Button
          type="button"
          onClick={async () => {
            await navigate({ to: "/pitches" })
          }}
          className="min-w-30"
          variant="outline"
          size="lg"
        >
          {t("create.cancel")}
        </Button>
        <Button
          className="min-w-30"
          variant="outline"
          type="button"
          disabled={!ValidateFormData() || isSubmitting || isSaving}
          onClick={onClickSave}
          size="lg"
        >
          {isSaving ? t("create.saving") : t("create.save")}
        </Button>
        <Button
          className="min-w-30"
          size="lg"
          type="button"
          disabled={!ValidateFormData() || isSubmitting || isSaving}
          onClick={onClickSubmit}
        >
          {isSubmitting ? t("create.submitting") : t("create.submit")}
        </Button>
      </div>
    </div>
  )
}
